import { api } from "./authService";

/**
 * Columns the importer understands. Anything else in the file (for example the
 * "url" column older templates carried) is ignored rather than rejected, so an
 * existing export can be imported unchanged.
 */
export const IMPORT_COLUMNS = ["track", "code", "title"];

const TRACK_VALUES = { IT: "IT", MEDI: "MEDI" };

/** Splits one CSV line, honouring quoted fields that contain commas. */
const splitCsvLine = (line) => {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      // "" inside a quoted field is a literal quote
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      cells.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(current);

  return cells.map((cell) => cell.trim());
};

/**
 * Maps header names to column positions. Falls back to the documented
 * track,code,title order when the file has no recognisable header.
 */
const resolveColumns = (headerCells) => {
  const normalized = headerCells.map((cell) =>
    cell.toLowerCase().replace(/^﻿/, "").trim(),
  );
  const index = {};
  IMPORT_COLUMNS.forEach((name) => {
    const at = normalized.indexOf(name);
    if (at !== -1) index[name] = at;
  });

  const hasHeader = index.title != null || index.track != null;
  if (hasHeader) return { index, hasHeader: true };

  return { index: { track: 0, code: 1, title: 2 }, hasHeader: false };
};

/** Builds a code from the title when the file leaves the column empty. */
const deriveCode = (title) => {
  const base = title
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "")
    .slice(0, 10);
  return base || `SUB${Date.now().toString().slice(-6)}`;
};

const subjectImportService = {
  /**
   * Imports subjects from a CSV file.
   *
   * track and title are required; code is optional and is derived from the
   * title when missing. Rows that cannot be imported are reported back per
   * line instead of being skipped silently.
   */
  importCsv: async (file) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((line) => line.trim());

    if (lines.length === 0) {
      throw new Error("CSV file is empty or has no content");
    }

    const { index, hasHeader } = resolveColumns(splitCsvLine(lines[0]));
    const dataLines = hasHeader ? lines.slice(1) : lines;

    if (dataLines.length === 0) {
      throw new Error("CSV file contains a header but no rows");
    }

    let imported = 0;
    let skipped = 0;
    const errors = [];

    for (let i = 0; i < dataLines.length; i++) {
      // Line number as the user sees it in their spreadsheet
      const lineNumber = hasHeader ? i + 2 : i + 1;
      const cells = splitCsvLine(dataLines[i]);
      const cellAt = (name) =>
        index[name] != null ? (cells[index[name]] || "").trim() : "";

      const title = cellAt("title");
      const track = cellAt("track");
      const code = cellAt("code");

      if (!title) {
        errors.push(`Line ${lineNumber}: title is required`);
        continue;
      }

      if (!track) {
        errors.push(`Line ${lineNumber}: track is required (IT or MEDI)`);
        continue;
      }

      const normalizedTrack = track.toUpperCase();
      if (!TRACK_VALUES[normalizedTrack]) {
        errors.push(
          `Line ${lineNumber}: unknown track "${track}" - use IT or MEDI`,
        );
        continue;
      }

      const finalTitle = title.slice(0, 100);
      const finalCode = (code || deriveCode(title)).slice(0, 10);

      try {
        await api.post("/subjects", {
          name: finalTitle,
          description: title.slice(0, 255),
          subject_code: finalCode,
          subject_version: "1.0",
          subject_type: TRACK_VALUES[normalizedTrack],
          track: "PFLICHT",
          year: 1,
          is_global: true,
          validation_status: "VALID",
          import_source: "CSV_IMPORT",
          is_system_subject: false,
        });
        imported++;
      } catch (error) {
        if (error.response?.status === 409) {
          // Already present - not worth failing the import over
          skipped++;
          continue;
        }
        errors.push(
          `Line ${lineNumber} (${finalCode}): ${
            error.response?.data?.message || error.message
          }`,
        );
      }
    }

    return {
      success: errors.length === 0,
      message:
        errors.length === 0
          ? `${imported} subject${imported === 1 ? "" : "s"} imported successfully`
          : `${imported} imported, ${errors.length} row${
              errors.length === 1 ? "" : "s"
            } could not be imported`,
      importedCount: imported,
      processedRows: dataLines.length,
      skippedCount: skipped,
      errors: errors.length > 0 ? errors : null,
    };
  },

  /**
   * The downloadable template. Kept next to the parser so the two can never
   * end up describing different columns.
   */
  buildTemplateCsv: () =>
    [
      IMPORT_COLUMNS.join(","),
      "IT,M117,Informatik und Netzwerke",
      "IT,,Betriebssysteme",
      "MEDI,M231,Gestaltung und Konzeption",
    ].join("\r\n") + "\r\n",

  // Alle Subjects löschen
  clearAllSubjects: async () => {
    try {
      // Get all subjects first
      const subjectsResponse = await api.get("/subjects");
      const subjects = subjectsResponse.data;

      let deleted = 0;
      for (const subject of subjects) {
        try {
          await api.delete(`/subjects/${subject.id}`);
          deleted++;
        } catch (error) {
          console.error(`Error deleting subject ${subject.id}:`, error);
        }
      }

      return {
        success: true,
        message: `${deleted} subjects deleted successfully`,
        deletedCount: deleted,
      };
    } catch (error) {
      console.error("Error clearing subjects:", error);
      throw error;
    }
  },
};

export default subjectImportService;
